/**
 * Botón "📞 Llamar ahora" para chat WA (Voice Commerce M6).
 *
 * Click → invoca el flujo click-to-call existente (call-center-start-call).
 * Idempotente: si ya hay llamada activa para este agente, el backend la
 * rechaza con mensaje claro.
 */

import { Button, Tooltip } from '@/lib/polaris';
import { PhoneIcon } from '@/lib/polaris-icons';
import { useAdminToast } from '@/contexts/AdminToastContext';
import { useStartAgentCall } from '@/hooks/useStartAgentCall';
import { normalizeDialInputToE164 } from '@/lib/phone/normalizeLeadPhone';
import { AppIconButton } from '@/components/admin/AppIconButton';

interface WaCallNowButtonProps {
  /** Phone E.164 del contacto. Si null/inválido, el botón se deshabilita. */
  contactPhone: string | null;
  contactName?: string | null;
  /**
   * Variante visual: 'icon' = botón Polaris compacto (header de la bandeja); 'text' = con texto;
   * 'chrome' = ícono pelado para una cabecera de color, del mismo tamaño que minimizar y cerrar.
   */
  variant?: 'icon' | 'text' | 'chrome';
}

const E164_REGEX = /^\+[1-9][0-9]{6,14}$/;

export function WaCallNowButton({ contactPhone, contactName, variant = 'icon' }: WaCallNowButtonProps) {
  const toast = useAdminToast();
  const mutation = useStartAgentCall();

  /*
   * 🪤 EL TELÉFONO CASI NUNCA LLEGA EN E.164. La conversación lo guarda como lo manda WhatsApp
   * ("51999888777") y un pedido como lo tipeó quien lo cargó ("999888777"). Validar el crudo
   * dejaba el botón deshabilitado casi siempre, sin decir por qué. Se normaliza con el MISMO
   * helper que usa el marcador, así los dos entienden lo mismo por "número válido".
   */
  const telefonoE164 = contactPhone ? normalizeDialInputToE164(contactPhone) : '';
  const phoneIsValid = E164_REGEX.test(telefonoE164);

  const handleClick = async () => {
    if (!phoneIsValid) return;
    try {
      const res = await mutation.mutateAsync({
        to_number: telefonoE164,
        source: 'wa_chat_header',
      });
      if (res.success) {
        toast.success(res.message ?? `Llamando a ${contactName ?? contactPhone}…`);
      }
    } catch (e) {
      toast.error(`No se pudo iniciar llamada: ${(e as Error).message}`);
    }
  };

  const tooltipContent = phoneIsValid
    ? `Llamar a ${contactName ?? contactPhone} desde Llamadas`
    : 'Sin teléfono E.164 válido';

  if (variant === 'chrome') {
    // Sin el botón de Polaris: sobre el verde de la cabecera traería su propio fondo y su propio
    // tamaño de ícono, y quedaría distinto de minimizar y cerrar, que están al lado.
    return (
      <AppIconButton
        icon={PhoneIcon}
        accessibilityLabel={tooltipContent}
        title={tooltipContent}
        onClick={() => { if (phoneIsValid && !mutation.isPending) void handleClick(); }}
        detenerPropagacion
      />
    );
  }

  if (variant === 'icon') {
    /*
     * 🪤 ANTES ERA EL `Button` DE POLARIS Y POR ESO SE VEÍA DISTINTO. En la cabecera del pedido queda
     * al lado del ícono de chat, que usa `AppIconButton`: uno chico y el otro grande. Lo señaló el
     * founder —«el icono de chats está chiquito y el de teléfono está grande»— y es exactamente el
     * problema que `AppIconButton` vino a resolver, esquivado porque le faltaban `disabled` y
     * `cargando`. Ahora los tiene, así que este usa el estándar y los dos íconos miden lo mismo por
     * construcción, no por coincidencia.
     *
     * `tono="base"` y `hover="oscuro"`: esta variante va sobre cabeceras claras. El default del
     * componente es para cabeceras de color, donde el hover tiene que ser blanco.
     */
    return (
      <Tooltip content={tooltipContent} dismissOnMouseOut>
        <AppIconButton
          icon={PhoneIcon}
          accessibilityLabel="Llamar ahora"
          title={tooltipContent}
          onClick={() => void handleClick()}
          disabled={!phoneIsValid}
          cargando={mutation.isPending}
          tono="base"
          hover="oscuro"
        />
      </Tooltip>
    );
  }

  return (
    <Button
      icon={PhoneIcon}
      onClick={handleClick}
      disabled={!phoneIsValid || mutation.isPending}
      loading={mutation.isPending}
      variant="primary"
    >
      Llamar ahora
    </Button>
  );
}
